import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { EXPERIENCES } from "../../data/content";

const Item = ({ exp, index, isOpen, onToggle }) => {
    return (
        <div
            data-testid={`experience-item-${exp.id}`}
            className="border-t-2 border-[var(--ink)] group"
        >
            <button
                onClick={onToggle}
                aria-expanded={isOpen}
                className="w-full text-left grid grid-cols-12 gap-x-3 gap-y-1 md:gap-6 items-center md:items-baseline py-5 md:py-6 px-1 md:px-2 hover:bg-[var(--surface)] transition-colors"
            >
                <div
                    className={`col-span-2 md:col-span-1 mono-meta md:pt-2 ${isOpen ? "text-[var(--brand)] font-semibold" : ""}`}
                >
                    0{index + 1}
                </div>
                <div className="col-span-8 md:col-span-6">
                    <h3
                        className="font-serif tracking-[-0.02em] leading-[0.95] text-[clamp(1.9rem,5vw,3.75rem)]"
                    >
                        {exp.company}
                    </h3>
                    <div className="mt-1.5 md:mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span
                            className={`mono-label !text-[10px] ${isOpen ? "text-[var(--brand)]" : "text-[var(--ink-soft)]"}`}
                        >
                            {exp.subCompany}
                        </span>
                    </div>
                    {/* Role + duration inline on mobile (hidden on desktop where it has its own column) */}
                    <div className="md:hidden mt-2">
                        <div className="mono-label !text-[10px]">
                            {exp.role}
                        </div>
                        <div className="mono-meta !text-[11px] mt-0.5">
                            {exp.duration}
                        </div>
                    </div>
                </div>
                <div className="hidden md:block col-span-4 pt-2">
                    <div className="mono-label">{exp.role}</div>
                    <div className="mono-meta mt-1">{exp.duration}</div>
                </div>
                <div className="col-span-2 md:col-span-1 flex justify-end md:pt-2">
                    <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className={`inline-flex items-center justify-center w-8 h-8 md:w-9 md:h-9 shrink-0 border-2 transition-colors ${
                            isOpen
                                ? "border-[var(--brand)] bg-[var(--brand)] text-[var(--page)]"
                                : "border-[var(--ink)] bg-[var(--surface)]"
                        }`}
                        aria-hidden="true"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2.2} />
                    </motion.span>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                            height: {
                                duration: 0.55,
                                ease: [0.22, 1, 0.36, 1],
                            },
                            opacity: { duration: 0.35, delay: 0.1 },
                        }}
                        className="overflow-hidden"
                    >
                        <div className="grid grid-cols-12 gap-3 md:gap-6 pb-10 md:pb-14 px-1 md:px-2">
                            <div className="col-span-12 md:col-start-2 md:col-span-4">
                                <div className="mono-label text-[var(--ink-soft)]">
                                    Location
                                </div>
                                <div className="mono-meta mt-1 mb-6">
                                    {exp.location}
                                </div>
                                <div className="mono-label text-[var(--ink-soft)]">
                                    Stack
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {exp.stack.map((s) => (
                                        <span
                                            key={s}
                                            className="mono-label !text-[10px] border-2 border-[var(--ink)] px-2 py-1 bg-[var(--surface)]"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="col-span-12 md:col-span-7">
                                <ul className="space-y-4 md:space-y-5">
                                    {exp.bullets.map((b, i) => (
                                        <motion.li
                                            key={i}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                                delay: 0.1 + i * 0.08,
                                                duration: 0.5,
                                            }}
                                            className="grid grid-cols-[24px_1fr] gap-3 items-baseline"
                                        >
                                            <span className="mono-meta !text-[10px] text-[var(--brand)]">
                                                0{i + 1}
                                            </span>
                                            <p className="font-serif text-xl md:text-2xl leading-[1.35] tracking-[-0.005em]">
                                                {b}
                                            </p>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export const Experience = () => {
    const [openId, setOpenId] = useState(EXPERIENCES[0].id);

    return (
        <section
            id="work"
            data-testid="work-section"
            className="relative pt-24 md:pt-36 pb-16 md:pb-24"
        >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="grid grid-cols-12 gap-4 md:gap-6 pb-10 md:pb-16"
                >
                    <div className="col-span-12 md:col-span-8">
                        <div className="mono-label mb-4 flex items-center gap-2">
                            <span className="inline-block w-2.5 h-2.5 bg-[var(--brand)]" />
                            Ch. 03 — Work
                        </div>
                        <h2 className="font-serif text-[clamp(2.2rem,5.5vw,4.25rem)] leading-[0.95] tracking-[-0.02em]">
                            Where I&rsquo;ve built
                            <br />
                            <span className="italic text-[var(--ink-soft)]">
                                things.
                            </span>
                        </h2>
                    </div>
                </motion.div>

                <div className="border-b-2 border-[var(--ink)]">
                    {EXPERIENCES.map((exp, i) => (
                        <Item
                            key={exp.id}
                            exp={exp}
                            index={i}
                            isOpen={openId === exp.id}
                            onToggle={() =>
                                setOpenId((cur) =>
                                    cur === exp.id ? null : exp.id,
                                )
                            }
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
